import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, MapPin, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { companyInfo, services, cities } from '../../data/mock';

const ZonesPage = () => {
  const primaryCity = cities.find(c => c.isPrimary);
  const otherCities = cities.filter(c => !c.isPrimary);

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">Zones d'intervention</li>
          </ol>
        </nav>

        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#262626] mb-4">
            Zones d'intervention - Vitrier Serrurier Val-de-Marne
          </h1>
          <p className="text-lg text-[#666666] max-w-2xl mx-auto">
            Intervention rapide à <strong>Vitry-sur-Seine</strong> et dans tout le Val-de-Marne (94). 
            Vitrier, serrurier et menuisier à votre service.
          </p>
        </div>

        {/* Primary City - VITRY */}
        {primaryCity && (
          <div className="mb-12">
            <Card className="bg-[#B91C1C] text-white overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <span className="inline-block bg-white/20 text-sm px-3 py-1 rounded-full mb-3">
                      Zone prioritaire
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2">{primaryCity.name}</h2>
                    <p className="text-white/80 mb-4">
                      Notre zone d'intervention principale. Dépannage en moins de 30 minutes garanti.
                    </p>
                    <div className="flex items-center gap-2 text-white/80">
                      <MapPin className="w-4 h-4" />
                      <span>{companyInfo.fullAddress}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                      <Button size="lg" className="w-full bg-white hover:bg-gray-100 text-[#B91C1C]">
                        <Phone className="w-5 h-5 mr-2" />
                        {companyInfo.phoneEmergency}
                      </Button>
                    </a>
                    <Link to={`/zones/${primaryCity.id}`}>
                      <Button size="lg" variant="outline" className="w-full border-white text-white hover:bg-white hover:text-[#B91C1C]">
                        Voir les services
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Other Cities Grid */}
        <h2 className="text-2xl font-bold text-[#262626] mb-6">Communes desservies</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {otherCities.map((city) => (
            <Link key={city.id} to={`/zones/${city.id}`}>
              <Card className="h-full hover:shadow-md transition-all hover:-translate-y-1 hover:border-[#B91C1C]/30">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#B91C1C]/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#B91C1C]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262626]">{city.name}</h3>
                      <p className="text-sm text-[#666666]">{city.postalCode}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-[#B91C1C] font-medium">
                    Voir les services
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-[#B91C1C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-[#B91C1C]" />
              </div>
              <h3 className="font-bold text-[#262626] mb-2">30 min d'intervention</h3>
              <p className="text-[#666666] text-sm">Arrivée sur place en moyenne</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-[#B91C1C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-[#B91C1C]" />
              </div>
              <h3 className="font-bold text-[#262626] mb-2">14 communes</h3>
              <p className="text-[#666666] text-sm">Couverture du Val-de-Marne</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-[#B91C1C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-[#B91C1C]" />
              </div>
              <h3 className="font-bold text-[#262626] mb-2">Devis gratuit</h3>
              <p className="text-[#666666] text-sm">Estimation sur place sans engagement</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ZoneDetailPage = () => {
  const { cityId } = useParams();
  const city = cities.find(c => c.id === cityId);

  if (!city) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold text-[#262626]">Ville non trouvée</h1>
        <Link to="/zones" className="text-[#B91C1C] mt-4 inline-block">Retour aux zones</Link>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li><Link to="/zones" className="hover:text-[#B91C1C]">Zones</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">{city.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <div className="bg-[#B91C1C] rounded-2xl p-8 md:p-12 text-white mb-12">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Urgence 24h/24
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Vitrier, Serrurier à {city.name} ({city.postalCode})
          </h1>
          <p className="text-lg text-white/80 mb-6 max-w-2xl">
            Services de vitrerie, serrurerie et menuiserie à {city.name}. 
            Intervention rapide et devis gratuit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
              <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-gray-100 text-[#B91C1C]">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#B91C1C]">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>

        {/* Services in this city */}
        <h2 className="text-2xl font-bold text-[#262626] mb-6">
          Nos services à {city.name}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service) => {
            // Utiliser les sous-services pour afficher les features
            const features = service.subServices 
              ? service.subServices.slice(0, 3).map(sub => sub.title)
              : [];
            
            return (
              <Card key={service.id} className="hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#262626] mb-2">{service.title}</h3>
                  <p className="text-[#666666] mb-4">{service.shortDesc}</p>
                  {features.length > 0 && (
                    <ul className="space-y-1 mb-4">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-[#262626]">
                          <CheckCircle className="w-4 h-4 text-[#B91C1C]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link to={`/services/${service.id}`} className="text-[#B91C1C] font-medium flex items-center">
                    En savoir plus
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Other cities */}
        <h2 className="text-2xl font-bold text-[#262626] mb-6">Autres communes proches</h2>
        <div className="flex flex-wrap gap-3">
          {cities.filter(c => c.id !== city.id).slice(0, 8).map((c) => (
            <Link 
              key={c.id} 
              to={`/zones/${c.id}`}
              className={`px-4 py-2 rounded-lg transition-colors ${
                c.isPrimary 
                  ? 'bg-[#B91C1C] text-white hover:bg-[#991B1B]'
                  : 'bg-white border hover:border-[#B91C1C] hover:bg-[#B91C1C]/5 text-[#262626]'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export { ZonesPage, ZoneDetailPage };